import { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';
import { useSettings } from '../../context/SettingsContext';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import OfflineSyncStatus from '../Offline/OfflineSyncStatus';

const AppHeader = ({ onAboutClick, onLogout }) => {
  const { settings } = useSettings();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const isOnline = useOnlineStatus();

  // Inicializa tema desde localStorage o preferencia del SO
  useEffect(() => {
    try {
      const stored = localStorage.getItem('theme');
      const prefersDark =
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      const enabled = stored ? stored === 'dark' : prefersDark;
      if (enabled) {
        document.documentElement.classList.add('dark');
        setIsDarkMode(true);
      } else {
        document.documentElement.classList.remove('dark');
        setIsDarkMode(false);
      }
    } catch (_) {
      document.documentElement.classList.remove('dark');
      setIsDarkMode(false);
    }
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => {
      const next = !prev;
      if (next) {
        document.documentElement.classList.add('dark');
        try {
          localStorage.setItem('theme', 'dark');
        } catch (_) {}
      } else {
        document.documentElement.classList.remove('dark');
        try {
          localStorage.setItem('theme', 'light');
        } catch (_) {}
      }
      return next;
    });
  };

  const handleLogoutClick = async () => {
    if (!isOnline) return;
    if (typeof onLogout === 'function') return onLogout();
    try {
      await signOut(auth);
    } catch (_) {
      // no-op
    }
    try {
      window.location.reload();
    } catch (_) {}
  };

  const handleAbout = () => {
    if (typeof onAboutClick === 'function') onAboutClick();
    setAboutOpen(true);
  };

  const headerName = settings?.storeName || settings?.ticketDisplayName || 'ValenPOS';
  const logoSrc =
    settings?.storeLogo ||
    'https://res.cloudinary.com/dbxgpws0g/image/upload/v1763331474/ChatGPT_Image_16_nov_2025_03_06_01_p.m._c7qlpk.png';

  return (
    <>
      <header className="sticky top-0 z-40 flex items-center justify-between bg-white/95 dark:bg-gray-900/95 px-4 py-3 md:px-6 shadow-sm backdrop-blur transition-colors duration-300">
        <div className="flex items-center gap-3">
          <img
            src={logoSrc}
            alt="Logo de la tienda"
            className="h-9 w-9 md:h-10 md:w-10 rounded-full shadow-sm object-contain bg-white"
            loading="lazy"
            decoding="async"
          />
          <div className="leading-tight">
            <span className="block text-lg font-bold text-gray-900 dark:text-white md:text-xl">
              {headerName}
            </span>
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-300 md:hidden">
              Panel ágil para móvil
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleDarkMode}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            title={isDarkMode ? 'Activar modo claro' : 'Activar modo oscuro'}
            aria-label="Cambiar tema"
          >
            {isDarkMode ? (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.325 6.675l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            )}
          </button>

          <button
            onClick={handleLogoutClick}
            disabled={!isOnline}
            title={
              isOnline
                ? 'Cerrar sesión'
                : 'Recupera la conexión a Internet para cerrar sesión.'
            }
            className={`hidden sm:inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 ${
              isOnline
                ? 'bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-400'
                : 'cursor-not-allowed bg-slate-100 text-slate-400 focus:ring-slate-300'
            }`}
          >
            Cerrar sesión
          </button>
          <button
            onClick={handleLogoutClick}
            disabled={!isOnline}
            title={
              isOnline
                ? 'Cerrar sesión'
                : 'Recupera la conexión a Internet para cerrar sesión.'
            }
            className={`sm:hidden inline-flex h-9 w-9 items-center justify-center rounded-full text-white transition focus:outline-none focus:ring-2 ${
              isOnline
                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-400'
                : 'cursor-not-allowed bg-slate-300 focus:ring-slate-300'
            }`}
            aria-label={
              isOnline
                ? 'Cerrar sesión'
                : 'Cerrar sesión deshabilitado mientras no hay conexión'
            }
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7" />
            </svg>
          </button>

          <button
            onClick={handleAbout}
            className="hidden sm:inline-flex items-center justify-center rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            Acerca de
          </button>
          <button
            onClick={handleAbout}
            className="sm:hidden inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
            aria-label="Acerca de"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      </header>

      <OfflineSyncStatus />

      {aboutOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6">
              <h2 className="text-2xl font-bold">ValenPOS</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Sistema de Punto de Venta</p>
              <div className="mt-4 space-y-1 text-sm">
                <p>
                  Desarrollado por <b>Valencode System</b> © 2025
                </p>
                <p>Versión: 1.0.0</p>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => setAboutOpen(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppHeader;
